import i18n from "i18n";
import queryString from "query-string";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ROLES } from "@infinitaslearning/module-he-common";
import { authRepository, productsListRepository } from "repositories";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { ToastMethods } from "components/ToastNotification";
import {
  ADMIN_ENVIRONMENT_ROUTES,
  AUTH_PAGES_ROUTES,
  GENERIC_ROUTES,
  PRODUCT_PAGES_ROUTES,
  TEACHER_HOME_PAGE_ROUTES,
} from "constants/routes";
import type { Login } from "models/auth/Login";
import { setAccess } from "persistence/access";

import { trackIdentifyEvent } from "analytics/MetabaseTracker/metabase-tracker";
import { USER_TYPE_PER_ROLE } from "analytics/constants/userTypes";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useProfile } from "hooks/useProfile";
import { useQueryParam } from "hooks/useQueryParam";
import { useSelectedProductId } from "hooks/useSelectedProductId";
import useMigrationStatus from "./useMigrationStatus";

export default (): void => {
  const [loggedUser, setLoggedUser] = useState<Login | null>(null);
  const [emailAddress, setEmailAddress] = useState<string>();

  const code = useQueryParam("code");
  const navigate = useNavigate();
  const { refreshFeatureFlagValues } = useFeatureFlags();

  useMigrationStatus(emailAddress);

  const {
    isRegistrationComplete,
    setIsRegistrationComplete,
    lastLoginCodeUsed,
    setLastLoginCodeUsed,
  } = useProfile();

  const lastSelectedProductId = useSelectedProductId();

  // Redirect user to application depending on their role and situation
  /* eslint consistent-return: off */
  const redirect = useCallback((user: Login): void => {
    const { role, schoolNumber } = user;

    if (!schoolNumber && role === ROLES.STUDENT) {
      navigate(AUTH_PAGES_ROUTES.USER_MISSING_INFO, { replace: true });
      return;
    }

    // If user was coming from a specific url, go back to that page
    const { state } = queryString.parse(window.location.search);

    if (state) {
      navigate(state as string, { replace: true });
      return;
    }

    // Redirect to main page depending on user role
    if (role === ROLES.TEACHER) {
      navigate(TEACHER_HOME_PAGE_ROUTES.HOME_PAGE, { replace: true });
      return;
    }

    if (role === ROLES.ADMIN) {
      navigate(ADMIN_ENVIRONMENT_ROUTES.USERS_PAGE, { replace: true });
      return;
    }

    productsListRepository
      .getProductList()
      .then((productsList) => {
        const lastSelectedProduct = productsList?.find(
          ({ id: productId }) => productId === lastSelectedProductId,
        );

        if (lastSelectedProduct) {
          navigate(
            buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS, {
              productId: +lastSelectedProduct.id,
            }),
            { replace: true },
          );
        } else {
          navigate(
            buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS, {
              productId: productsList[0].id,
            }),
            { replace: true },
          );
        }
      })
      .catch(() => {
        navigate(GENERIC_ROUTES.HOME_PAGE, { replace: true });
      });
  }, []);

  // Call "validate" endpoint to perform login
  useEffect(() => {
    // Compare last login code used with the one in the URL to prevent "validate" endpoint
    // from being called twice. This is currently necessary because the route seems to be
    // re-rendering right after the "refreshFeatureFlagValues" call.
    if (code && lastLoginCodeUsed === code) {
      return;
    }

    setLastLoginCodeUsed(code);
    setIsRegistrationComplete(null);
    authRepository
      .validateLoginUser(code as string)
      .then((response: Login) => {
        const { id: userId, jwt, role, refreshToken, email } = response;

        setEmailAddress(email);

        // Store current user data in cookies
        setAccess({ jwt, refreshToken });

        // Determine user sync is still in progress
        setIsRegistrationComplete(false);

        // Keep track of successful login in Metabase
        trackIdentifyEvent({ id: userId.toString(), type: USER_TYPE_PER_ROLE[role] });

        // Store logged user data in state to redirect when needed
        setLoggedUser(response);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:user.validate.error"), "error");
      });
  }, [code, lastLoginCodeUsed]);

  // Redirect user only if registration has been completed
  useEffect(() => {
    if (loggedUser && isRegistrationComplete) {
      redirect(loggedUser);

      // Force refresh of feature flags after successful login
      refreshFeatureFlagValues();
    }
  }, [loggedUser, isRegistrationComplete]);
};
